import React from "react";
import { Calendar, PlusCircle, Loader, Trash2 } from "lucide-react";
import ReusableDropdown from "../../../../../components/ReusableDropdown";
import { useAuth } from "../../../../../context/AuthContext";

/* ─── helpers ─── */
const getStatusColor = (status, theme) => {
  switch (status) {
    case "approved": return theme === "dark" ? "#10b981" : "#059669";
    case "pending":  return theme === "dark" ? "#f59e0b" : "#d97706";
    case "rejected": return theme === "dark" ? "#ef4444" : "#dc2626";
    default:         return "#2e7cba";
  }
};
const normalizeStatus    = (v) => (typeof v === "string" ? v.trim().toLowerCase() : "");
const normalizeLeaveType = (v) => (typeof v === "string" ? v.trim().toLowerCase() : "");
const getStatusText    = (status, t) => ({ pending: t.pending, approved: t.approved, rejected: t.rejected }[status] || status);
const getLeaveTypeText = (type, t)   => ({ annual: t.annual, sick: t.sick, maternity: t.maternity, emergency: t.emergency, wfh: t.wfh }[type] || type);

/* ─────────────── */

const ManageStep = ({
  theme, isRTL, language, t,
  empLeaves, empLeavesLoading,
  deletingLeaveIds, onDeleteLeave,
  newLeaveForm, onLeaveFormChange,
  empQuota, empQuotaLoading,
  createLeaveLoading, onCreateLeave,
}) => {
  // ✅ Admin-level feature gating — registry "leaves.remove" ("Delete") and
  // "leaves.createleave" ("Create Employee Leave"). Bypassed roles
  // (admin/superadmin) always pass.
  const { hasFeatureAccess } = useAuth();
  const canRemove = hasFeatureAccess("leaves", "remove");
  const canCreateLeave = hasFeatureAccess("leaves", "createleave");

  /* balance helper */
  const getLeaveTypeBalance = (leaveType) => {
    if (!empQuota) return null;
    const map = {
      annual:    { quota: "annualQuota",    used: "annualUsed" },
      sick:      { quota: "sickQuota",      used: "sickUsed" },
      maternity: { quota: "maternityQuota", used: "maternityUsed" },
      emergency: { quota: "emergencyQuota", used: "emergencyUsed" },
    };
    const keys = map[leaveType];
    if (!keys) return null;
    const total = Number(empQuota[keys.quota]) || 0;
    const used  = Number(empQuota[keys.used]) || 0;
    return { total, used, remaining: Math.max(0, total - used) };
  };

  const inputStyle = (extra = {}) => ({
    width: "100%", boxSizing: "border-box", padding: "10px 12px",
    background: theme === "dark" ? "rgba(255,255,255,0.07)" : "#f9fafb",
    border: theme === "dark" ? "1.5px solid rgba(255,255,255,0.15)" : "1.5px solid rgba(0,0,0,0.12)",
    borderRadius: "10px", color: theme === "dark" ? "#fff" : "#1a1a1a",
    fontSize: "14px", outline: "none",
    colorScheme: theme === "dark" ? "dark" : "light",
    ...extra,
  });

  const fieldLabel = (
    <style>{`
      .manage-field-label {
        display: block; font-size: 12px; font-weight: 600;
        text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px;
      }
    `}</style>
  );

  const labelStyle = {
    display: "block", fontSize: "12px", fontWeight: "600",
    color: theme === "dark" ? "rgba(255,255,255,0.7)" : "#6b7280",
    textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px",
  };

  const leaveTypeItems = [
    { id: "annual",     name: t.annual },
    { id: "sick",       name: t.sick },
    { id: "maternity",  name: t.maternity },
    { id: "emergency",  name: t.emergency },
    { id: "partialDay", name: t.partialDay },
    { id: "wfh",        name: t.wfh },
    { id: "custom",     name: t.custom },
  ];

  const balance = getLeaveTypeBalance(newLeaveForm.leaveType);

  return (
    <div>
      {/* ── Existing Leaves ── */}
      <div style={{ marginBottom: "24px" }}>
        <h4 style={{ margin: "0 0 14px 0", fontWeight: "700", fontSize: "15px", color: theme === "dark" ? "#fff" : "#1a1a1a", display: "flex", alignItems: "center", gap: "8px" }}>
          <Calendar size={16} color="#2e7cba" />
          {t.manualLeaves}
        </h4>

        {empLeavesLoading ? (
          <div style={{ textAlign: "center", padding: "32px 0", color: theme === "dark" ? "rgba(255,255,255,0.5)" : "#9ca3af" }}>
            <Loader size={28} className="spin" style={{ margin: "0 auto 10px" }} />
            <div>{t.loading}</div>
          </div>
        ) : empLeaves.length === 0 ? (
          <div style={{ textAlign: "center", padding: "32px 0", color: theme === "dark" ? "rgba(255,255,255,0.5)" : "#9ca3af", fontSize: "14px" }}>
            {t.noLeavesFound}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "240px", overflowY: "auto" }}>
            {empLeaves.map((lv) => (
              <div
                key={lv.id}
                style={{
                  display: "flex", alignItems: "center", gap: "12px",
                  padding: "12px 14px", borderRadius: "12px",
                  background: theme === "dark" ? "rgba(255,255,255,0.05)" : "#f9fafb",
                  border: theme === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.06)",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: "600", fontSize: "14px", color: theme === "dark" ? "#fff" : "#1a1a1a", marginBottom: "3px" }}>
                    {getLeaveTypeText(normalizeLeaveType(lv.leaveType || lv.type), t)}
                    <span style={{ marginLeft: 8, padding: "2px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "600", background: `${getStatusColor(normalizeStatus(lv.status), theme)}20`, color: getStatusColor(normalizeStatus(lv.status), theme) }}>
                      {getStatusText(normalizeStatus(lv.status), t)}
                    </span>
                  </div>
                  <div style={{ fontSize: "12px", color: theme === "dark" ? "rgba(255,255,255,0.5)" : "#9ca3af" }}>
                    {new Date(lv.startDate).toLocaleDateString(language === "ar" ? "ar-EG" : "en-US")}
                    {" → "}
                    {new Date(lv.endDate).toLocaleDateString(language === "ar" ? "ar-EG" : "en-US")}
                  </div>
                </div>
                {canRemove && (
                <button
                  onClick={() => onDeleteLeave(lv.id)}
                  disabled={deletingLeaveIds.has(lv.id)}
                  style={{
                    background: "none", border: "1.5px solid #ef4444", borderRadius: "8px",
                    color: "#ef4444", cursor: "pointer", padding: "6px 10px",
                    display: "flex", alignItems: "center", gap: "5px",
                    fontSize: "12px", fontWeight: "600",
                    opacity: deletingLeaveIds.has(lv.id) ? 0.5 : 1,
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => { if (!deletingLeaveIds.has(lv.id)) e.currentTarget.style.background = "#ef444420"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}
                >
                  {deletingLeaveIds.has(lv.id)
                    ? <Loader size={14} className="spin" />
                    : <Trash2 size={14} />}
                </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Create New Leave Form ── */}
      {canCreateLeave && (
      <div style={{ borderTop: theme === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.08)", paddingTop: "20px" }}>
        <h4 style={{ margin: "0 0 14px 0", fontWeight: "700", fontSize: "15px", color: theme === "dark" ? "#fff" : "#1a1a1a", display: "flex", alignItems: "center", gap: "8px" }}>
          <PlusCircle size={16} color="#2e7cba" />
          {t.addNewLeave}
        </h4>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px,1fr))", gap: "12px", marginBottom: "14px" }}>

          {/* Leave Type */}
          <div style={{ position: "relative", zIndex: 500 }}>
            <label style={labelStyle}>{t.leaveType}</label>
            <ReusableDropdown
              items={leaveTypeItems}
              value={newLeaveForm.leaveType || ""}
              onChange={(val) => onLeaveFormChange("leaveType", val || "")}
              onSelect={(item) => onLeaveFormChange("leaveType", item.id)}
              getItemKey={(item) => item.id}
              getItemLabel={(item) => item.name}
              placeholder={t.selectLeaveType}
              theme={theme}
              language={language}
              maxItems={10}
              showAllOnArrowClick={true}
              showClear={!!newLeaveForm.leaveType}
              onClear={() => { onLeaveFormChange("leaveType", ""); onLeaveFormChange("customLeaveType", ""); }}
              inputStyle={{ fontSize: "14px", padding: "10px 12px", borderRadius: "10px" }}
            />

            {/* Balance indicator */}
            {balance && (
              <div style={{ marginTop: "6px", display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", fontWeight: "600", color: balance.remaining <= 2 ? "#ef4444" : (theme === "dark" ? "rgba(255,255,255,0.6)" : "#6b7280") }}>
                <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: balance.remaining <= 2 ? "#ef4444" : "#10b981", flexShrink: 0 }} />
                {t.balanceRemaining}: {empQuotaLoading ? "..." : `${balance.remaining} ${t.days}`}
                <span style={{ opacity: 0.6 }}>{t.quotaLabel} {balance.total}</span>
              </div>
            )}

            {/* Custom type input */}
            {newLeaveForm.leaveType === "custom" && (
              <input
                type="text"
                value={newLeaveForm.customLeaveType}
                onChange={(e) => onLeaveFormChange("customLeaveType", e.target.value)}
                placeholder={t.customTypePlaceholder}
                style={{ marginTop: "8px", ...inputStyle() }}
              />
            )}
          </div>

          {/* Date fields */}
          {newLeaveForm.leaveType === "partialDay" ? (
            <>
              {/* Single date */}
              <div>
                <label style={labelStyle}>{t.date}</label>
                <input
                  type="date"
                  value={newLeaveForm.startDate}
                  onChange={(e) => onLeaveFormChange("startDate", e.target.value)}
                  style={inputStyle()}
                />
              </div>
              {/* Half-day type */}
              <div>
                <label style={labelStyle}>{t.halfDayType}</label>
                <div style={{ position: "relative", zIndex: 600 }}>
                  <ReusableDropdown
                    items={[
                      { id: "",             name: t.selectHalfDayType },
                      { id: "first_half",   name: t.firstHalf },
                      { id: "second_half",  name: t.secondHalf },
                    ]}
                    value={newLeaveForm.halfDayType}
                    onChange={(id) => onLeaveFormChange("halfDayType", id)}
                    placeholder={t.selectHalfDayType}
                    maxItems={10}
                    theme={theme}
                    language={language}
                    showAllOnArrowClick={false}
                    showClear={false}
                    inputStyle={{ padding: "10px 12px", borderRadius: "10px", border: theme === "dark" ? "1.5px solid rgba(255,255,255,0.15)" : "1.5px solid rgba(0,0,0,0.12)", background: theme === "dark" ? "rgba(255,255,255,0.03)" : "#f9fafb", color: theme === "dark" ? "#fff" : "#1a1a1a" }}
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Start Date */}
              <div>
                <label style={labelStyle}>{t.startDate}</label>
                <input
                  type="date"
                  value={newLeaveForm.startDate}
                  onChange={(e) => onLeaveFormChange("startDate", e.target.value)}
                  style={inputStyle()}
                />
              </div>
              {/* End Date */}
              <div>
                <label style={labelStyle}>{t.endDate}</label>
                <input
                  type="date"
                  value={newLeaveForm.endDate}
                  onChange={(e) => onLeaveFormChange("endDate", e.target.value)}
                  style={inputStyle()}
                />
              </div>
            </>
          )}
        </div>

        {/* Opt‑out notification checkbox */}
        <div style={{ marginTop: "12px", marginBottom: "14px", display: "flex", alignItems: "center" }}>
          <input
            type="checkbox"
            id="skipConfirmationNotification"
            checked={newLeaveForm.skipConfirmationNotification}
            onChange={(e) => onLeaveFormChange("skipConfirmationNotification", e.target.checked)}
            style={{ marginRight: "8px", width: "16px", height: "16px" }}
          />
          <label htmlFor="skipConfirmationNotification" style={{ ...labelStyle, marginBottom: 0, cursor: "pointer" }}>
            {t.skipNotificationLabel || "Skip confirmation notification"}
          </label>
        </div>

        {/* Create button */}
        <button
          onClick={onCreateLeave}
          disabled={createLeaveLoading}
          style={{
            background: createLeaveLoading ? "rgba(46,124,186,0.5)" : "linear-gradient(135deg,#2e7cba,#2e7cba)",
            color: "#fff", border: "none", borderRadius: "12px",
            padding: "12px 24px", fontSize: "14px", fontWeight: "700",
            cursor: createLeaveLoading ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: "8px",
            boxShadow: createLeaveLoading ? "none" : "0 4px 16px rgba(46,124,186,0.3)",
            transition: "all 0.3s ease",
          }}
        >
          {createLeaveLoading
            ? <><Loader size={16} className="spin" />{t.creating}</>
            : <><PlusCircle size={16} />{t.createLeave}</>}
        </button>
      </div>
      )}
    </div>
  );
};

export default ManageStep;